import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  useColorMode,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

const CreateUserForm = ({ isOpen, onClose, onUserCreated }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    first_name: "",
    last_name: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const { colorMode } = useColorMode();
  const toast = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    if (formError) {
      setFormError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setFormError("");

    try {
      const accessToken = localStorage.getItem("access_token");
      console.log("Sending request with data:", formData);
      console.log("Access token:", accessToken);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/accounts/users`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      console.log("Response status:", response.status);
      console.log("Response data:", data);

      if (!response.ok) {
        if (response.status === 422) {
          console.log("Handling 422 validation errors:", data);
          // Format the error message to be more user-friendly
          const errorMessage = Array.isArray(data.detail)
            ? data.detail
                .map((error) => {
                  const field = error.loc[1]; // Get the field name
                  const message = error.msg.replace("Value error, ", ""); // Remove "Value error, " prefix
                  return `${
                    field.charAt(0).toUpperCase() + field.slice(1)
                  }: ${message}`;
                })
                .join("\n")
            : data.detail;
          setFormError(errorMessage);
          return;
        }
        throw new Error(data.detail || "Failed to create user");
      }

      toast({
        title: t("auth.createUser.success"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onUserCreated();
      onClose();
      setFormData({
        username: "",
        password: "",
        email: "",
        first_name: "",
        last_name: "",
      });
    } catch (err) {
      console.error("Error in handleSubmit:", err);
      setFormError(err.message);
      toast({
        title: t("auth.createUser.errorTitle"),
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        bg={colorMode === "dark" ? "gray.700" : "white"}
        color={colorMode === "dark" ? "white" : "gray.800"}
      >
        <ModalHeader>{t("auth.createUser.title")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {formError && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{formError}</AlertDescription>
              </Box>
            </Alert>
          )}
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired isInvalid={!!errors.username}>
                <FormLabel>{t("auth.createUser.username")}</FormLabel>
                <Input
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder={t("auth.createUser.usernamePlaceholder")}
                  bg={colorMode === "dark" ? "gray.600" : "white"}
                  borderColor={colorMode === "dark" ? "gray.500" : "gray.200"}
                />
                {errors.username && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.username}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.password}>
                <FormLabel>{t("auth.createUser.password")}</FormLabel>
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t("auth.createUser.passwordPlaceholder")}
                  bg={colorMode === "dark" ? "gray.600" : "white"}
                  borderColor={colorMode === "dark" ? "gray.500" : "gray.200"}
                />
                {errors.password && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.password}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.email}>
                <FormLabel>{t("auth.createUser.email")}</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t("auth.createUser.emailPlaceholder")}
                  bg={colorMode === "dark" ? "gray.600" : "white"}
                  borderColor={colorMode === "dark" ? "gray.500" : "gray.200"}
                />
                {errors.email && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.email}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={!!errors.first_name}>
                <FormLabel>{t("auth.createUser.firstName")}</FormLabel>
                <Input
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder={t("auth.createUser.firstNamePlaceholder")}
                  bg={colorMode === "dark" ? "gray.600" : "white"}
                  borderColor={colorMode === "dark" ? "gray.500" : "gray.200"}
                />
                {errors.first_name && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.first_name}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={!!errors.last_name}>
                <FormLabel>{t("auth.createUser.lastName")}</FormLabel>
                <Input
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder={t("auth.createUser.lastNamePlaceholder")}
                  bg={colorMode === "dark" ? "gray.600" : "white"}
                  borderColor={colorMode === "dark" ? "gray.500" : "gray.200"}
                />
                {errors.last_name && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.last_name}
                  </Text>
                )}
              </FormControl>

              <Button
                type="submit"
                colorScheme="blue"
                width="full"
                isLoading={loading}
                loadingText={t("auth.createUser.loading")}
              >
                {t("auth.createUser.submit")}
              </Button>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateUserForm;
